// src/modules/leads/route-planner/PermissionOnboarding.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaMapMarkerAlt, FaBell, FaRoute, FaCheck, FaTimes, FaShieldAlt,
} from 'react-icons/fa';
import { subscribeToPush, isPushSupported } from '../../../services/pushService';
import {
  OnboardingOverlay, OnboardingCard,
  OnboardingIconRing, OnboardingTitle, OnboardingDesc,
  OnboardingButton, OnboardingSkip, OnboardingDots, OnboardingDot,
  OnboardingProgress, PermissionRow, PermissionIcon,
  PermissionText, PermissionBadge,
} from './routePlannerStyles';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STEPS = { LOADING: 0, PERMISSIONS: 1, REQUESTING: 2 };

async function checkGeoPermission() {
  // navigator.permissions is not available on iOS Safari for geolocation
  try {
    if (navigator.permissions?.query) {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', 'prompt'
    }
  } catch { /* iOS throws — fall through */ }
  return 'prompt'; // assume unknown = will prompt
}

function checkNotifPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

// ─── Component ──────────────────────────────────────────────────────────────

const PermissionOnboarding = ({ isGoogleLoaded, googleError, onComplete }) => {
  const [step, setStep] = useState(STEPS.LOADING);
  const [geoStatus, setGeoStatus] = useState('pending');   // pending | granted | denied
  const [notifStatus, setNotifStatus] = useState('pending');
  const [requesting, setRequesting] = useState(false);
  const [mapsProgress, setMapsProgress] = useState(10);
  const progressRef = useRef(null);

  // ── Advance past loading when maps finish OR fail ─────────────────────────
  const mapsReady = isGoogleLoaded || !!googleError;

  useEffect(() => {
    if (mapsReady) {
      setMapsProgress(100);
      const t = setTimeout(() => setStep(STEPS.PERMISSIONS), 600);
      return () => clearTimeout(t);
    }

    // Fake progress while maps load
    let tick = 10;
    progressRef.current = setInterval(() => {
      tick += Math.random() * 12;
      if (tick > 85) tick = 85;
      setMapsProgress(Math.round(tick));
    }, 400);

    return () => clearInterval(progressRef.current);
  }, [mapsReady]);

  // ── Safety: if still on loading after 12s, force advance ──────────────────
  useEffect(() => {
    if (step !== STEPS.LOADING) return;
    const safety = setTimeout(() => setStep(STEPS.PERMISSIONS), 12000);
    return () => clearTimeout(safety);
  }, [step]);

  // ── Check current permission states once on the permissions step ──────────
  useEffect(() => {
    if (step !== STEPS.PERMISSIONS) return;

    (async () => {
      const geo = await checkGeoPermission();
      setGeoStatus(geo === 'granted' ? 'granted' : geo === 'denied' ? 'denied' : 'pending');

      const notif = checkNotifPermission();
      if (notif === 'granted') setNotifStatus('granted');
      else if (notif === 'denied') setNotifStatus('denied');
      else if (notif === 'unsupported') setNotifStatus('granted'); // nothing to do
      else setNotifStatus('pending');

      // If both already granted, skip straight through
      if (geo === 'granted' && (notif === 'granted' || notif === 'unsupported')) {
        onComplete();
      }
    })();
  }, [step, onComplete]);

  // ── Request all permissions sequentially ─────────────────────────────────
  const handleContinue = useCallback(async () => {
    setRequesting(true);

    // 1) Request geolocation — on iOS this triggers the native permission dialog
    if (geoStatus !== 'granted' && geoStatus !== 'denied') {
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            () => { setGeoStatus('granted'); resolve(); },
            (err) => {
              setGeoStatus(err.code === 1 ? 'denied' : 'granted');
              // Don't reject on timeout — the permission is still "prompt"
              resolve();
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
          );
        });
      } catch { /* continue regardless */ }
    }

    // 2) Request notification permission
    if (notifStatus !== 'granted' && notifStatus !== 'denied' && 'Notification' in window) {
      try {
        const result = await Notification.requestPermission();
        setNotifStatus(result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'pending');

        // If granted and push is supported, subscribe in background
        if (result === 'granted' && isPushSupported()) {
          subscribeToPush().catch(() => {});
        }
      } catch { /* continue regardless */ }
    }

    // Small pause so user sees the check marks animate
    await new Promise(r => setTimeout(r, 500));
    setRequesting(false);
    onComplete();
  }, [geoStatus, notifStatus, onComplete]);

  const handleSkip = useCallback(() => {
    onComplete();
  }, [onComplete]);

  // ── Render: Loading Maps ─────────────────────────────────────────────────
  if (step === STEPS.LOADING) {
    return (
      <OnboardingOverlay>
        <OnboardingCard>
          <OnboardingIconRing>
            <FaRoute />
          </OnboardingIconRing>
          <OnboardingTitle>Route Planner</OnboardingTitle>
          <OnboardingDesc>Loading maps and preparing your experience...</OnboardingDesc>
          <OnboardingProgress percent={mapsProgress} />
        </OnboardingCard>
      </OnboardingOverlay>
    );
  }

  // ── Render: Permission Request ───────────────────────────────────────────
  const needsGeo = geoStatus !== 'granted' && geoStatus !== 'denied';
  const needsNotif = notifStatus !== 'granted' && notifStatus !== 'denied';
  const allDone = !needsGeo && !needsNotif;

  return (
    <OnboardingOverlay>
      <OnboardingCard>
        <OnboardingDots>
          <OnboardingDot active={false} />
          <OnboardingDot active={true} />
        </OnboardingDots>

        <OnboardingIconRing color="#3d5afe">
          <FaShieldAlt />
        </OnboardingIconRing>

        <OnboardingTitle>Quick Setup</OnboardingTitle>
        <OnboardingDesc>
          We need a couple of permissions to give you the best routing experience.
        </OnboardingDesc>

        {/* Location row */}
        <PermissionRow status={geoStatus}>
          <PermissionIcon color="#3d5afe">
            <FaMapMarkerAlt />
          </PermissionIcon>
          <PermissionText>
            <div className="title">Location Access</div>
            <div className="desc">
              {geoStatus === 'granted'
                ? 'Location enabled'
                : geoStatus === 'denied'
                ? 'Denied — you can enter an address manually'
                : 'Auto-detect your starting point for routes'}
            </div>
          </PermissionText>
          <PermissionBadge status={geoStatus}>
            {geoStatus === 'granted' && <FaCheck />}
            {geoStatus === 'denied' && <FaTimes />}
          </PermissionBadge>
        </PermissionRow>

        {/* Notification row */}
        <PermissionRow status={notifStatus}>
          <PermissionIcon color="#9b59b6">
            <FaBell />
          </PermissionIcon>
          <PermissionText>
            <div className="title">Notifications</div>
            <div className="desc">
              {notifStatus === 'granted'
                ? 'Notifications enabled'
                : notifStatus === 'denied'
                ? 'Denied — you can enable later in settings'
                : 'Get alerts for new leads and updates'}
            </div>
          </PermissionText>
          <PermissionBadge status={notifStatus}>
            {notifStatus === 'granted' && <FaCheck />}
            {notifStatus === 'denied' && <FaTimes />}
          </PermissionBadge>
        </PermissionRow>

        <div style={{ height: 24 }} />

        {allDone ? (
          <OnboardingButton variant="primary" onClick={onComplete}>
            <FaRoute /> Open Route Planner
          </OnboardingButton>
        ) : (
          <OnboardingButton
            variant="primary"
            onClick={handleContinue}
            disabled={requesting}
          >
            {requesting ? 'Setting up...' : 'Allow & Continue'}
          </OnboardingButton>
        )}

        <OnboardingSkip onClick={handleSkip}>
          {allDone ? '' : 'Skip for now'}
        </OnboardingSkip>
      </OnboardingCard>
    </OnboardingOverlay>
  );
};

export default PermissionOnboarding;
